import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { useAuth } from "@/hooks/useAuth";
import { PromptsManager } from "@/components/admin/PromptsManager";
import { FrameworksManager } from "@/components/admin/FrameworksManager";
import { AIToolsManager } from "@/components/admin/AIToolsManager";
import SiteSettingsManager from "@/components/admin/SiteSettingsManager";

const isDev = import.meta.env.DEV;

const Admin = () => {
  const { user, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [adminCheckError, setAdminCheckError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
      return;
    }

    if (user && !isAdmin && !adminCheckError) {
      checkAdminStatus();
    }
  }, [user, loading]); // Removed navigate, added conditions to prevent re-checks

  const checkAdminStatus = async () => {
    try {
      setCheckingAdmin(true);
      setAdminCheckError(null);
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user?.id)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        if (error.message?.includes("Failed to fetch")) {
          setAdminCheckError("Unable to connect to the server. Please check your connection and try again.");
        } else {
          setAdminCheckError(error.message);
        }
        throw error;
      }
      
      setIsAdmin(!!data);
      setAdminCheckError(null);
    } catch (error) {
      if (isDev) console.error("Error checking admin status:", error);
      setIsAdmin(false);
    } finally {
      setCheckingAdmin(false);
    }
  };

  if (loading || checkingAdmin) {
    return (
      <Layout>
        <div className="py-12 text-center">
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return null; // Will redirect to auth
  }

  if (adminCheckError) {
    return (
      <Layout>
        <div className="py-12">
          <div className="container mx-auto px-4">
            <Card className="max-w-2xl mx-auto text-center p-8">
              <CardHeader>
                <CardTitle className="text-destructive">Connection Error</CardTitle>
                <CardDescription className="text-base mt-2">
                  {adminCheckError}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex gap-3 justify-center">
                <Button onClick={checkAdminStatus}>
                  Retry
                </Button>
                <Button onClick={() => navigate("/")} variant="outline">
                  Go Home
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return (
      <Layout>
        <div className="py-12">
          <div className="container mx-auto px-4">
            <Card className="max-w-2xl mx-auto text-center p-8">
              <CardHeader>
                <CardTitle>Access Denied</CardTitle>
                <CardDescription>
                  You don't have administrator privileges. Contact the site owner to request access.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => navigate("/")} variant="outline">
                  Go Home
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">Content Management</h1>
            <p className="text-muted-foreground">
              Manage all your website content from this dashboard.
            </p>
          </div>

          <Tabs defaultValue="prompts" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="prompts">Prompts</TabsTrigger>
              <TabsTrigger value="frameworks">Frameworks</TabsTrigger>
              <TabsTrigger value="ai-tools">AI Tools</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="prompts" className="mt-6">
              <PromptsManager />
            </TabsContent>

            <TabsContent value="frameworks" className="mt-6">
              <FrameworksManager />
            </TabsContent>

            <TabsContent value="ai-tools" className="mt-6">
              <AIToolsManager />
            </TabsContent>
            
            <TabsContent value="settings" className="mt-6">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Account</CardTitle>
                    <CardDescription>Manage your account settings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      variant="destructive" 
                      onClick={async () => {
                        await supabase.auth.signOut();
                        navigate("/");
                      }}
                    >
                      Log Out
                    </Button>
                  </CardContent>
                </Card>
                <SiteSettingsManager />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default Admin;